import { ReactNode } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import styled from 'styled-components'

import { breakpoints, colors } from '@/styles'
import useRole from '@/hooks/use-role'
import { makeMainLayout } from '@/layouts/main'
import { show as showLoginModal } from '@/features/Auth/login-modal'
import { HeaderTheme } from '@/features/Header'
import RentReport from '@/features/Home/RentReport'
import guidesMobile from '@/features/Home/RentReport/images/guides-mobile.webp'
import guidesWide from '@/features/Home/RentReport/images/guides-wide.webp'
import guides from '@/features/Home/RentReport/images/guides.webp'
import {
  AutomateRenting,
  HeroSection,
  MultiLayeredSecurity,
  PaymentOptions,
  PlatformForRenters,
  RentalRecord,
  VerifiedListings
} from '@/features/Renters'
import { Grid } from '@/features/Renters/elements'
import faqsMobile from '@/features/Renters/images/faqs-mobile@2x.png'
import faqsWide from '@/features/Renters/images/faqs-wide@2x.png'
import faqs from '@/features/Renters/images/faqs@2x.png'
import MainWrapper from '@/features/Structure/main-wrapper'
import ApplicationDownload from '@/components/application-download'
import { Button, ButtonProps } from '@/components/button'
import Head from '@/components/head'

const Wrapper = styled(MainWrapper)`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${colors.background};
`

const Content = styled.div`
  background: ${colors.white};
  display: flex;
  flex-direction: column;
  align-items: center;
`

const gridBreakpoint = breakpoints.card

const SectionHeader = styled.div`
  margin: 170px 60px 80px;
  text-align: center;

  @media (max-width: ${breakpoints.tabletLandscape}) {
    margin: 110px 60px 50px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    margin: 100px 25px 50px;
    text-align: left;
  }
`

const SectionTitle = styled.h2`
  color: ${colors.regular};
  font-family: futura-pt-bold, Futura, sans-serif;
  font-weight: 700;
  font-size: 57px;
  line-height: 54px;
  letter-spacing: -0.1px;
  margin: 0 0 20px;

  @media (max-width: ${breakpoints.tabletLandscape}) {
    font-size: 46px;
    line-height: 43px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    font-size: 34px;
    letter-spacing: -0.09px;
    line-height: 34px;
  }
`

const SectionDescription = styled.p`
  color: ${colors.regular};
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
  letter-spacing: -0.5px;
  max-width: 800px;
  margin: 0;

  @media (max-width: ${breakpoints.tabletLandscape}) {
    font-size: 20px;
    line-height: 28px;
    max-width: 680px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    font-size: 18px;
    line-height: 24px;
    max-width: 325px;
  }
`

const RentalContent = styled.div`
  background: ${colors.background};
`

const RentalReports = styled.div``

const NeighbourhoodGuides = styled.div``

const ResidentialTenancy = styled.div``

const ButtonContainer = styled.div`
  padding: 80px 0 170px;

  a {
    text-decoration: none;
  }

  @media (max-width: ${breakpoints.tabletLandscape}) {
    padding: 60px 0 120px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    padding: 50px 0 100px;
  }
`

const MktButton = ({
  id,
  theme = 'secondary',
  children,
  ...rest
}: ButtonProps & { children: ReactNode }) => (
  <Button id={id} theme={theme} width="190px" radius={50} shadow {...rest}>
    {children}
  </Button>
)

const SignUpButton = ({ id, ...rest }: ButtonProps) => {
  const { t } = useTranslation('renters')
  const role = useRole()
  const isLoggedIn = !!role
  if (isLoggedIn) return null
  return (
    <MktButton
      id={id}
      onClick={() =>
        showLoginModal({
          mode: 'register',
          eventId: 'web-signup-submit-rentersignupforfree',
          t
        })
      }
      {...rest}
    >
      {t('renters:Sign up for free')}
    </MktButton>
  )
}

const GreySection = styled.div`
  background-color: ${colors.background};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Renters = () => {
  const { t } = useTranslation('renters')
  const router = useRouter()
  const metaTitle = t(
    `renters:Canada's Safest Rental Website | Renters | liv.rent`
  )
  const metaDescription = t(
    'renters:Search verified apartments, houses and homes for rent from trusted landlords, ID-verified by our team of rental experts.'
  )

  const reportTexts = {
    topTitle: t('renters:Rent reports'),
    topDesc: t(
      'renters:Stay on top of the latest rental data and trends for Vancouver, Toronto, Alberta, and Montreal.'
    ),
    topLink: 'https://liv.rent/blog/rent-reports/',
    topId: 'renter-rent-reports-click',
    botLeftTitle: t('renters:Neighbourhood guides'),
    botLeftDesc: t(
      'renters:Love where you rent. Learn more about the neighbourhoods we call home.'
    ),
    botLeftLink: 'https://liv.rent/blog/neighbourhoods/',
    botLeftId: 'renter-neighbourhood-guides-click',
    botRightTitle: t('renters:Renter FAQs'),
    botRightDesc: t(
      `renters:Renting doesn't have to be hard. Equip yourself with knowledge so that you can navigate the rental process with ease.`
    ),
    botRightLink: 'https://liv.rent/blog/renters/',
    botRightId: 'renter-residential-tenancy-click'
  }

  const guidesImages = [guides, guidesWide, guidesMobile]
  const faqsImages = [faqs, faqsWide, faqsMobile]

  const redirectToListings = () => {
    router.push({
      pathname: '/rental-listings'
    })
  }

  return (
    <Wrapper>
      <Head title={metaTitle} description={metaDescription} path="/renters" />
      <HeroSection onClick={redirectToListings} />
      <Content>
        <SectionHeader>
          <SectionTitle>
            {t('renters:A smarter – and safer – way to rent')}
          </SectionTitle>
        </SectionHeader>
        <Grid breakpoint={gridBreakpoint}>
          <VerifiedListings />
          <MultiLayeredSecurity />
          <RentalRecord breakpoint={gridBreakpoint} />
          <AutomateRenting breakpoint={gridBreakpoint} />
          <PaymentOptions />
          <PlatformForRenters breakpoint={gridBreakpoint} />
        </Grid>
        <ButtonContainer>
          <SignUpButton id="btn-signup-renter-bottom" />
        </ButtonContainer>
        <GreySection>
          <SectionHeader>
            <SectionTitle>{t('renters:Resources for renters')}</SectionTitle>
            <SectionDescription>
              {t(
                'renters:Putting our renters first means empowering you with the latest rental data, news, and information.'
              )}
            </SectionDescription>
          </SectionHeader>
          <RentReport
            reportTexts={reportTexts}
            botLeftImages={guidesImages}
            botRightImages={faqsImages}
          />
          <ButtonContainer>
            <Button
              as="a"
              id="btn-renter-viewall-click"
              href="https://liv.rent/blog/"
              theme="secondary"
              width="190px"
              radius={50}
              shadow
            >
              {t('renters:View all')}
            </Button>
          </ButtonContainer>
        </GreySection>
      </Content>
      <RentalContent>
        <RentalReports />
        <NeighbourhoodGuides />
        <ResidentialTenancy />
      </RentalContent>
      <ApplicationDownload />
    </Wrapper>
  )
}

export const getStaticProps = async ({ locale }: { locale: string }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['translation', 'renters']))
    }
  }
}

const headerConfig = {
  showSearch: true,
  theme: HeaderTheme.TRANSPARENT
}

Renters.Layout = makeMainLayout({ headerConfig })

export default Renters
